/**
 * useDataFetching Hook
 * 
 * Custom React hook for fetching office employee salary and bonus data from the backend API.
 * Provides data fetching functionality with loading and error state management.
 * 
 * Features:
 * - Initial data fetch on mount based on dataType
 * - Filtered data fetching with optional parameters (year, month/period, employee code)
 * - Loading state management
 * - Error handling and display
 * - Automatic data type switching (salary vs bonus)
 * 
 * Used by:
 * - DataDisplay component for office employee data display
 * 
 * @module useDataFetching
 */

import { useState, useEffect, useCallback } from 'react';
import { SalaryData, BonusData, DataType } from '../types/DataDisplayTypes';
import fileUploadService from '../../../../../services/FileUploadService';

/**
 * useDataFetching Hook
 * 
 * Custom hook for fetching office employee salary and bonus data.
 * Fetches data from Spring Boot backend API using fileUploadService.
 * 
 * @param dataType - Current data type ('salary' or 'bonus')
 * @returns Object containing salaryData, bonusData, loading, error, and fetchFilteredData function
 * 
 * @example
 * ```typescript
 * const { salaryData, bonusData, loading, error, fetchFilteredData } = useDataFetching('salary');
 * ```
 */
export const useDataFetching = (dataType: DataType) => {
  /**
   * State management:
   * - salaryData: Array of salary records
   * - bonusData: Array of bonus records
   * - loading: Loading state indicator (starts as true for initial fetch)
   * - error: Error message if fetch fails (null if no error)
   */
  const [salaryData, setSalaryData] = useState<SalaryData[]>([]);
  const [bonusData, setBonusData] = useState<BonusData[]>([]);
  /** Full unfiltered datasets used only for filter dropdown options */
  const [allSalaryData, setAllSalaryData] = useState<SalaryData[]>([]);
  const [allBonusData, setAllBonusData] = useState<BonusData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch real data from Spring Boot API using fileUploadService.
   * 
   * Automatically fetches all data on mount and when dataType changes.
   * Handles both salary and bonus data types.
   * 
   * @effect
   * Runs on mount and when dataType changes.
   */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (dataType === 'salary') {
          /**
           * Use fileUploadService to fetch all salary data from Spring Boot backend.
           * Calls fetchSalaryDataWithFilters() with no parameters to get all records.
           */
          const data = await fileUploadService.fetchSalaryDataWithFilters();
          setSalaryData(data);
          setAllSalaryData(data);
        } else {
          /**
           * Use fileUploadService to fetch all bonus data from Spring Boot backend.
           * Calls fetchBonusDataWithFilters() with no parameters to get all records.
           */
          const data = await fileUploadService.fetchBonusDataWithFilters();
          setBonusData(data);
          setAllBonusData(data);
        }
        setError(null);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        /**
         * Show backend error message when available (e.g. from API response).
         * Otherwise show generic message to check backend server.
         */
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'データの取得に失敗しました。バックエンドサーバーが起動しているか確認してください。';
        setError(message);
        if (dataType === 'salary') {
          setSalaryData([]);
          setAllSalaryData([]);
        } else {
          setBonusData([]);
          setAllBonusData([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dataType]);

  const refreshFilterOptions = useCallback(async () => {
    try {
      if (dataType === 'salary') {
        const data = await fileUploadService.fetchSalaryDataWithFilters();
        setAllSalaryData(data);
      } else {
        const data = await fileUploadService.fetchBonusDataWithFilters();
        setAllBonusData(data);
      }
    } catch (err) {
      console.error('Error refreshing filter options:', err);
    }
  }, [dataType]);

  /**
   * Function to fetch filtered data from backend.
   * 
   * Fetches data with optional filter parameters:
   * - year: Filter by year (number, parsed from string)
   * - month: Filter by month for salary data (number, parsed from string)
   * - period: Filter by period for bonus data (string)
   * - employeeCode: Filter by employee code (string)
   * 
   * Passes appropriate parameters based on dataType:
   * - Salary: year, month, undefined (period), employeeCode
   * - Bonus: year, undefined (month), period, employeeCode
   * 
   * @param year - Optional year filter (string, will be parsed to number)
   * @param month - Optional month filter for salary (string, will be parsed to number)
   * @param period - Optional period filter for bonus (string)
   * @param employeeCode - Optional employee code filter (string)
   * 
   * @example
   * ```typescript
   * // Fetch salary data for 2024, January, employee code "EMP001"
   * await fetchFilteredData('2024', '1', undefined, 'EMP001');
   * 
   * // Fetch bonus data for 2024, period "summer", employee code "EMP001"
   * await fetchFilteredData('2024', undefined, 'summer', 'EMP001');
   * ```
   */
  const fetchFilteredData = async (year?: string, month?: string, period?: string, employeeCode?: string) => {
    try {
      setLoading(true);
      
      /**
       * Parse string parameters to numbers where needed.
       * Undefined values remain undefined for optional filters.
       */
      const yearNum = year ? parseInt(year) : undefined;
      const monthNum = month ? parseInt(month) : undefined;
      const hasFilters = !!(year || month || period || employeeCode);

      /**
       * Fetch data based on data type.
       * Pass appropriate parameters: salary uses month, bonus uses period.
       */
      if (dataType === 'salary') {
        /**
         * Fetch salary data with filters: year, month, undefined (period), employeeCode.
         */
        const data = await fileUploadService.fetchSalaryDataWithFilters(yearNum, monthNum, undefined, employeeCode);
        setSalaryData(data);
        if (!hasFilters) {
          setAllSalaryData(data);
        }
      } else {
        /**
         * Fetch bonus data with filters: year, undefined (month), period, employeeCode.
         */
        const data = await fileUploadService.fetchBonusDataWithFilters(yearNum, period, undefined, employeeCode);
        setBonusData(data);
        if (!hasFilters) {
          setAllBonusData(data);
        }
      }
      setError(null);
    } catch (err: any) {
      console.error('Error fetching filtered data:', err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'フィルターされたデータの取得に失敗しました。';
      setError(message);
      if (dataType === 'salary') {
        setSalaryData([]);
      } else {
        setBonusData([]);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Return hook values and functions.
   * 
   * Provides:
   * - salaryData: Array of salary records (empty if dataType is 'bonus')
   * - bonusData: Array of bonus records (empty if dataType is 'salary')
   * - loading: Loading state indicator
   * - error: Error message (null if no error)
   * - fetchFilteredData: Function to fetch filtered data
   */
  return {
    salaryData,
    bonusData,
    allSalaryData,
    allBonusData,
    loading,
    error,
    fetchFilteredData,
    refreshFilterOptions,
  };
}; 